// ProductionRuns.jsx - Fixed: manager access + detailed production summary by product + Stock Summary & Returns
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../apiClient";

const ProductionRuns = () => {
  const [allowedProducts, setAllowedProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [productionHistory, setProductionHistory] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [formData, setFormData] = useState({
    produced_at: new Date().toISOString().split("T")[0],
    product_id: "",
    quantity: "",
    unit: "pcs",
    batch_code: "",
    notes: "",
    ingredient_usage: {},
  });
  const [returnForm, setReturnForm] = useState({
    date: new Date().toISOString().split("T")[0],
    product_name: "",
    quantity: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const userRole = localStorage.getItem("userRole");
  const navigate = useNavigate();

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };
  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  // Fetch products (all products — configurable for resale)
  const fetchProducts = useCallback(async () => {
    try {
      const allProducts = await apiClient.get("/products/");
      const filtered = allProducts;
      setAllowedProducts(filtered);
      if (filtered.length > 0 && !formData.product_id) {
        setFormData((prev) => ({ ...prev, product_id: filtered[0].id }));
      }
      if (filtered.length > 0 && !returnForm.product_name) {
        setReturnForm((prev) => ({ ...prev, product_name: `${filtered[0].name}${filtered[0].size ? ' ' + filtered[0].size : ''}` }));
      }
    } catch (err) {
      showError("⚠️ Could not load products.");
    }
  }, [formData.product_id, returnForm.product_name]);

  // Fetch ingredients
  const fetchIngredients = useCallback(async () => {
    try {
      const data = await apiClient.get("/production/ingredients/");
      setIngredients(data);
      const usage = {};
      data.forEach((ing) => (usage[ing.id] = ""));
      setFormData((prev) => ({ ...prev, ingredient_usage: usage }));
    } catch (err) {
      showError("⚠️ Could not load ingredients.");
    }
  }, []);

  // Fetch current stock
  const fetchStock = useCallback(async () => {
    try {
      const data = await apiClient.get("/stock/");
      setStockData(data);
    } catch (err) {
      // Removed console.error
    }
  }, []);

  // Fetch production history (for summary)
  const fetchProductionHistory = useCallback(async () => {
    try {
      const runs = await apiClient.get("/production/");
      setProductionHistory(runs);
    } catch (err) {
      // Removed console.error
    }
  }, []);

  // Fetch stock summary
  const fetchStockSummary = useCallback(async () => {
    try {
      const data = await apiClient.get("/production/stock-summary");
      setStockSummary(data);
    } catch (err) {
      // Removed console.error
    }
  }, []);

  // Auth guard
  useEffect(() => {
    if (userRole !== "admin" && userRole !== "manager" && userRole !== "production_staff") {
      navigate("/dashboard");
    }
  }, [userRole, navigate]);

  // Initial data load — allow admin, manager, and production_staff
  useEffect(() => {
    if (userRole === "admin" || userRole === "manager" || userRole === "production_staff") {
      fetchProducts();
      fetchIngredients();
      fetchStock();
      fetchProductionHistory();
      fetchStockSummary();
    }
  }, [userRole, fetchProducts, fetchIngredients, fetchStock, fetchProductionHistory, fetchStockSummary]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("usage_")) {
      const ingredientId = parseInt(name.split("_")[1], 10);
      setFormData((prev) => ({
        ...prev,
        ingredient_usage: {
          ...prev.ingredient_usage,
          [ingredientId]: value === "" ? "" : parseFloat(value),
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          name === "product_id" || name === "quantity"
            ? value === ""
              ? ""
              : Number(value)
            : value,
      }));
    }
  };

  // Handle return form changes
  const handleReturnChange = (e) => {
    const { name, value } = e.target;
    setReturnForm((prev) => ({
      ...prev,
      [name]:
        name === "quantity"
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }));
  };

  // Submit production run
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.product_id || !formData.quantity || formData.quantity <= 0) {
      showError("⚠️ Please select a product and enter a valid quantity.");
      return;
    }

    const stockMap = {};
    stockData.forEach((item) => {
      stockMap[item.id] = {
        available: item.available,
        name: item.name,
        unit: item.unit,
      };
    });

    for (const [ingIdStr, amount] of Object.entries(formData.ingredient_usage)) {
      const ingId = parseInt(ingIdStr, 10);
      const amt = parseFloat(amount);
      if (amt > 0) {
        const stock = stockMap[ingId];
        if (stock && amt > stock.available) {
          showError(`❌ Not enough ${stock.name}: available = ${stock.available} ${stock.unit}`);
          return;
        }
      }
    }

    const ingredientsToSend = [];
    for (const [ingId, amount] of Object.entries(formData.ingredient_usage)) {
      if (amount && parseFloat(amount) > 0) {
        ingredientsToSend.push({
          ingredient_id: parseInt(ingId, 10),
          amount: parseFloat(amount),
        });
      }
    }

    const payload = {
      product_id: formData.product_id,
      quantity: Number(formData.quantity),
      unit: formData.unit,
      produced_at: formData.produced_at,
      batch_code: formData.batch_code || null,
      notes: formData.notes || null,
      ingredients: ingredientsToSend,
    };

    setLoading(true);
    try {
      await apiClient.post("/production/", payload);
      showSuccess("✅ Production run saved successfully!");

      const usage = {};
      ingredients.forEach((ing) => (usage[ing.id] = ""));
      setFormData({
        produced_at: new Date().toISOString().split("T")[0],
        product_id: allowedProducts.length > 0 ? allowedProducts[0].id : "",
        quantity: "",
        unit: "pcs",
        batch_code: "",
        notes: "",
        ingredient_usage: usage,
      });

      fetchStock();
      fetchProductionHistory();
      fetchStockSummary(); // refresh stock summary
    } catch (err) {
      const message = err?.response?.data?.detail || "Failed to save production run.";
      showError(`❌ ${message}`);
    } finally {
      setLoading(false);
    }
  };

  // Submit product return
  const handleReturnSubmit = async (e) => {
    e.preventDefault();

    if (!returnForm.product_name || !returnForm.quantity || returnForm.quantity <= 0) {
      showError("⚠️ Please select a product and enter a valid quantity.");
      return;
    }

    const payload = {
      date: returnForm.date,
      product_name: returnForm.product_name,
      quantity: Number(returnForm.quantity),
      notes: returnForm.notes || null,
    };

    setReturnLoading(true);
    try {
      await apiClient.post("/product-returns/", payload);
      showSuccess("✅ Return recorded successfully!");

      setReturnForm({
        date: new Date().toISOString().split("T")[0],
        product_name: allowedProducts.length > 0 ? `${allowedProducts[0].name}${allowedProducts[0].size ? ' ' + allowedProducts[0].size : ''}` : "",
        quantity: "",
        notes: "",
      });

      fetchStockSummary(); // refresh stock summary
    } catch (err) {
      const message = err?.response?.data?.detail || "Failed to record return.";
      showError(`❌ ${message}`);
    } finally {
      setReturnLoading(false);
    }
  };

  // Get stock info for display
  const getStockInfo = (ingredientId) => {
    const stockItem = stockData.find((s) => s.id === ingredientId);
    return stockItem ? stockItem.available : null;
  };

  // Compute production totals by product
  const productionSummary = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const productMap = {};
    allowedProducts.forEach(p => {
      productMap[p.id] = p.name;
    });

    const periods = {
      daily: { start: todayStr, label: "Today" },
      weekly: { start: startOfWeek.toISOString().split("T")[0], label: "This week" },
      monthly: { start: startOfMonth.toISOString().split("T")[0], label: "This month" }
    };

    const result = {};

    Object.entries(periods).forEach(([key, config]) => {
      const totals = {};
      productionHistory.forEach(run => {
        const runDate = run.produced_at.split("T")[0];
        if (runDate >= config.start) {
          const name = productMap[run.product_id] || `Product ${run.product_id}`;
          totals[name] = (totals[name] || 0) + run.quantity;
        }
      });
      result[key] = totals;
    });

    return result;
  }, [productionHistory, allowedProducts]);

  // Format summary as readable string
  const formatSummary = (totals) => {
    const entries = Object.entries(totals);
    if (entries.length === 0) return "–";
    return entries.map(([name, qty]) => `${name} x${qty}`).join(", ");
  };

  if (userRole !== "admin" && userRole !== "manager" && userRole !== "production_staff") return null;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto bg-[#F5E9D8] min-h-screen">
      {/* Page Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#2D2F5E]">🏭 Production & Ingredient Usage</h2>
        <p className="text-sm text-[#2D2F5E] opacity-70 mt-1">
          Log production batches and track ingredient consumption
        </p>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-100 border border-green-300 text-green-700 p-3 rounded-lg text-center font-medium animate-pulse mb-4">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 p-3 rounded-lg text-center font-medium mb-4">
          {error}
        </div>
      )}

      {/* Form Card */}
      <div className="bg-white p-5 rounded-lg shadow-sm border border-[#2D2F5E] border-opacity-20">
        {allowedProducts.length === 0 || ingredients.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D2F5E]"></div>
            <p className="text-[#2D2F5E] mt-2">Loading...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">📅 Date</label>
              <input
                type="date"
                name="produced_at"
                value={formData.produced_at}
                onChange={handleChange}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
              />
            </div>

            {/* Product */}
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">🍞 Product</label>
              <select
                name="product_id"
                value={formData.product_id}
                onChange={handleChange}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white appearance-none"
              >
                {allowedProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.size ? `(${p.size})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Quantity & Unit */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2D2F5E] mb-1">🔢 Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="e.g. 100"
                  min="1"
                  step="1"
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2D2F5E] mb-1">📏 Unit</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  placeholder="e.g. pcs, kg"
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                />
              </div>
            </div>

            {/* Batch Code & Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2D2F5E] mb-1">🔖 Batch Code (optional)</label>
                <input
                  type="text"
                  name="batch_code"
                  value={formData.batch_code}
                  onChange={handleChange}
                  placeholder="e.g. T20251230-01"
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2D2F5E] mb-1">📝 Notes (optional)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="e.g. Extra crispy"
                  rows="2"
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                />
              </div>
            </div>

            {/* Production Totals Summary (detailed by product) */}
            <div className="pt-2 pb-3 border-t border-gray-200">
              <p className="text-xs text-[#2D2F5E] opacity-80">
                📊 <strong>Today:</strong> {formatSummary(productionSummary.daily)}<br />
                <strong>This week:</strong> {formatSummary(productionSummary.weekly)}<br />
                <strong>This month:</strong> {formatSummary(productionSummary.monthly)}
              </p>
            </div>

            {/* Ingredient Usage */}
            <div className="pt-2 border-t border-[#2D2F5E] border-opacity-20">
              <h3 className="text-lg font-bold text-[#2D2F5E] mb-2">🧂 Ingredient Usage</h3>
              <p className="text-xs text-[#2D2F5E] opacity-70 mb-2">
                {userRole === "admin"
                  ? "Enter amounts used. Available stock shown in green."
                  : "Enter amounts used for this production batch."}
              </p>
              <div className="space-y-3">
                {ingredients.map((ing) => {
                  const available = getStockInfo(ing.id);
                  const entered = formData.ingredient_usage[ing.id] || "";
                  const isOver = entered && available !== null && parseFloat(entered) > available;
                  return (
                    <div key={ing.id} className="flex flex-col sm:flex-row sm:items-end gap-3">
                      <div className="flex-1">
                        <label className="block text-sm text-[#2D2F5E]">{ing.name} ({ing.unit})</label>
                        <input
                          type="number"
                          name={`usage_${ing.id}`}
                          value={entered}
                          onChange={handleChange}
                          placeholder="e.g. 10"
                          step="0.1"
                          min="0"
                          className={`w-full p-2 border rounded text-[#2D2F5E] bg-white mt-1 ${
                            isOver
                              ? "border-red-500 bg-red-50"
                              : "border-[#2D2F5E] border-opacity-30"
                          }`}
                        />
                      </div>
                      {userRole === "admin" && available !== null && (
                        <div className="text-right">
                          <span className={`text-sm font-medium ${isOver ? "text-red-700" : "text-green-700"}`}>
                            Available: {available.toFixed(1)} {ing.unit}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full px-4 py-2 rounded-lg transition shadow font-medium ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#F76B4C] hover:bg-[#E65A3C] text-white"
              }`}
            >
              {loading ? "⏳ Saving..." : "✅ Save Production Run"}
            </button>
          </form>
        )}
      </div>

      {/* ==========================================
         STOCK SUMMARY & RETURNS SECTION (BOTTOM)
         ========================================== */}
      <div className="mt-6 bg-white p-5 rounded-lg shadow-sm border border-[#2D2F5E] border-opacity-20">
        <h3 className="text-xl font-bold text-[#2D2F5E] mb-4">📊 Tortilla & Gruby Stock Summary</h3>
        
        {/* Stock Summary Table */}
        {stockSummary.length === 0 ? (
          <p className="text-[#2D2F5E] opacity-70 text-center py-4">No stock data yet</p>
        ) : (
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#2D2F5E] text-white">
                  <th className="p-3 text-left text-sm">Product</th>
                  <th className="p-3 text-right text-sm">Made</th>
                  <th className="p-3 text-right text-sm">Delivered</th>
                  <th className="p-3 text-right text-sm">Returns</th>
                  <th className="p-3 text-right text-sm">Available</th>
                </tr>
              </thead>
              <tbody>
                {stockSummary.map((item, idx) => (
                  <tr key={idx} className="border-b border-[#2D2F5E] border-opacity-10">
                    <td className="p-3 text-[#2D2F5E] font-medium">{item.product_name}</td>
                    <td className="p-3 text-right text-[#2D2F5E]">{item.total_made}</td>
                    <td className="p-3 text-right text-[#2D2F5E]">{item.total_delivered}</td>
                    <td className="p-3 text-right text-[#2D2F5E]">{item.total_returns}</td>
                    <td className={`p-3 text-right font-bold ${
                      item.available < 0 ? "text-red-600" : "text-green-600"
                    }`}>
                      {item.available}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Record Return Form */}
        <div className="pt-4 border-t border-[#2D2F5E] border-opacity-20">
          <h4 className="text-lg font-bold text-[#2D2F5E] mb-3">↩️ Record Product Return</h4>
          <form onSubmit={handleReturnSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2D2F5E] mb-1">📅 Date</label>
                <input
                  type="date"
                  name="date"
                  value={returnForm.date}
                  onChange={handleReturnChange}
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2D2F5E] mb-1">🍞 Product</label>
                <select
                  name="product_name"
                  value={returnForm.product_name}
                  onChange={handleReturnChange}
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white appearance-none"
                >
                  {allowedProducts.map((p) => (
                    <option key={p.id} value={`${p.name}${p.size ? ' ' + p.size : ''}`}>
                      {p.name} {p.size ? `(${p.size})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">🔢 Quantity</label>
              <input
                type="number"
                name="quantity"
                value={returnForm.quantity}
                onChange={handleReturnChange}
                placeholder="e.g. 10"
                min="1"
                step="1"
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2D2F5E] mb-1">📝 Notes (optional)</label>
              <textarea
                name="notes"
                value={returnForm.notes}
                onChange={handleReturnChange}
                placeholder="e.g. Driver returned unsold items"
                rows="2"
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
              />
            </div>
            <button
              type="submit"
              disabled={returnLoading}
              className={`w-full px-4 py-2 rounded-lg transition shadow font-medium ${
                returnLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#F76B4C] hover:bg-[#E65A3C] text-white"
              }`}
            >
              {returnLoading ? "⏳ Saving..." : "✅ Record Return"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductionRuns;